import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { createChart, LineSeries } from 'lightweight-charts';
import useDraggable from '../hooks/useDraggable';
import useTrackPageView from '../hooks/useTrackPageView';
import TradeChartModal from './TradeChartModal';
import SweepHelp from './SweepHelp';
import { useTranslation } from '../i18n/LanguageContext';
import { useTheme } from '../hooks/useTheme';
import { useUserSettings } from '../hooks/useUserSettings';

const API_URL = '/api';

// Kullanıcının Ayarlar > Genel'den seçtiği UTC ofseti (dakika) — bkz. useUserSettings hook'u.
// Grafikte gösterim, backtest tarih girişi/çıkışı ve backend'in chart.js route'u AYNI ofseti kullanır.
const toLocal = (ms, offsetMinutes) => Math.floor(ms / 1000) + offsetMinutes * 60;

// Periyot/strateji isimleri ve config alan etiketleri `t()`'a bağlı olduğu için (bileşen dışında
// hook çağrılamaz) fonksiyon olarak tanımlanır — her bileşen kendi `t`'sini geçirir.
const getIntervals = (t) => [
  { label: t('backtest.interval1m'),  value: '1m'  },
  { label: t('backtest.interval5m'),  value: '5m'  },
  { label: t('backtest.interval15m'), value: '15m' },
  { label: t('backtest.interval30m'), value: '30m' },
  { label: '1h',   value: '1H'  },
  { label: '4h',   value: '4H'  },
  { label: '1D',   value: '1D'  },
];
// Strateji adları teknik/marka isimleri sayılır — her iki dilde de aynı kalır (HelpModal.jsx'teki
// aynı yaklaşım).
const STRATEGIES = [
  { value: 'range_hma_adx',    label: 'Range Bar+HMA+ADX' },
  { value: 'rsi_rsima',        label: 'RSI+SMA' },
  { value: 'rsima50',          label: 'SMA-50 Seviyesi' },
  { value: 'trend_pullback_r', label: 'Trend-Pullback+R' },
];
// Geçmiş listesindeki eski kayıtlar artık kaldırılmış bir strateji (rsi50, hma_adx) içerebilir —
// filtre/etiket bunu da tanısın diye STRATEGIES'in üzerine ayrıca eklenir (seçim listesinde
// GÖSTERİLMEZ, sadece geçmiş kayıtlarda karşılaşılırsa okunabilir bir etiket sağlar).
const STRATEGY_LABEL_MAP = { ...Object.fromEntries(STRATEGIES.map(s => [s.value, s.label])), rsi50: 'RSI-50 Seviyesi', hma_adx: 'HMA+ADX' };
const getRankOptions = (t) => [
  { value: 'totalPnl',       label: t('backtest.rankTotalPnl') },
  { value: 'winRate',        label: t('backtest.rankWinRate') },
  { value: 'profitFactor',   label: t('backtest.rankProfitFactor') },
  { value: 'expectancy',     label: t('backtest.rankExpectancy') },
  { value: 'maxDrawdownPct', label: t('backtest.rankMaxDrawdown') },
];

// Stratejiye özel config alanları. `def` = Tekli Test'te tek değer önerisi (bazı alanlarda gerçek
// backtest bulgularına göre sistem varsayılanından FARKLI, daha iyi çalışan bir değerdir — sadece
// bu formun placeholder'ı, arka planın asıl varsayılanını DEĞİŞTİRMEZ). `sweepEx` = Parametre
// Taraması'nda gösterilecek, gerçekten anlamlı bir tarama aralığı örneği (SweepHelp.jsx'teki
// "Aralık"/"Önerilen" metinleriyle TUTARLI tutulmalı).
const getStrategyConfigFields = (t) => ({
  range_hma_adx: [
    { key: 'hma_period',         label: t('backtest.fieldHmaPeriod'),  def: '21', sweepEx: '13,21,34' },
    { key: 'adx_period',         label: t('backtest.fieldAdxPeriod'),  def: '14', sweepEx: '9,14,21' },
    { key: 'adx_threshold',      label: t('backtest.fieldAdxThreshold'), def: '0',  sweepEx: '0,20,25' },
    { key: 'range_box_atr_mult', label: t('backtest.fieldBoxMult'), def: '1.0', sweepEx: '0.5,1.0,2.0' },
  ],
  rsi_rsima: [
    { key: 'rsi_deadzone', label: t('backtest.fieldRsiDeadzone'), def: '0', sweepEx: '0,1,3' },
    { key: 'tp_pct',       label: t('backtest.fieldTpPct'),       def: '0', sweepEx: '0,1,2' },
  ],
  // adx_threshold/rsi_trigger/pullback_atr_mult'un `def` değerleri, gerçek BTCUSDT backtest'inde
  // (Parametre Taraması ile) bulunan en iyi kombinasyona göre seçildi — bkz. SweepHelp.jsx'teki
  // "gerçek veriyle test edilmiş bulgu" notu.
  trend_pullback_r: [
    { key: 'adx_period',        label: t('backtest.fieldAdxPeriod'),            def: '14',  sweepEx: '9,14,21' },
    { key: 'adx_threshold',     label: t('backtest.fieldAdxThreshold'),                def: '25',  sweepEx: '0,20,25' },
    { key: 'pullback_atr_mult', label: t('backtest.fieldPullbackMult'), def: '0.7', sweepEx: '0.5,0.7,1.0' },
    { key: 'rsi_trigger',       label: t('backtest.fieldRsiTrigger'),       def: '55',  sweepEx: '45,52,55' },
  ],
  // RSI/RSIMA periyodu sabit (14/14, ayardan değiştirilemez). rsi_deadzone HEM giriş bandını
  // HEM çıkış eşiğini belirler — ama giriş artık ham seviye simetriği DEĞİL, zone-geçiş/
  // edge-trigger'dır: RSIMA önce banda uğramalı, dışına çıktığı anda açılır (bkz. analyzer.js'teki
  // rsima50 dalı). rsi_rsima ile AYNI şekilde: ATR yedek-sigorta fallback'ı YOK (kullanıcı kararı) — TP%/SL%
  // (getCommonConfigFields'ten gelen sl_pct dahil) bağımsız olarak 0/boş ise KESİNLİKLE kapalı.
  rsima50: [
    { key: 'rsi_deadzone', label: t('backtest.fieldRsiDeadzone'), def: '0', sweepEx: '0,3,5' },
    { key: 'tp_pct',       label: t('backtest.fieldTpPct'),       def: '0', sweepEx: '0,1,2' },
  ],
});
// Her iki stratejide de geçerli ortak alanlar (risk). CHOP/Min.Ödül:Risk/Günlük Kayıp Limiti
// kullanıcı kararıyla backtest formundan kaldırıldı (nadiren değiştirilen, karmaşıklık katan
// alanlar) — backend kendi varsayılanlarını kullanır (bkz. backtestEngine.js).
const getCommonConfigFields = (t) => [
  { key: 'atr_period',         label: t('backtest.fieldAtrPeriod'),           def: '14',   sweepEx: '9,14,21' },
  { key: 'atr_sl_mult',        label: t('backtest.fieldSlMult'),       def: '1.2',  sweepEx: '0.8,1.2,2.0' },
  { key: 'sl_pct',             label: t('backtest.fieldSlPct'),                    def: '0',    sweepEx: '0,0.5,1' },
];
// rsi_rsima/rsima50: ATR yedek-sigorta fallback'ı YOK (kullanıcı kararı) — atr_period/atr_sl_mult
// hiç anlamsız, formdan hariç tutulur. sl_pct AYNEN kalır ama bu 2 stratejide 0/boş "ATR kullan"
// değil "SL KAPALI" anlamına gelir (bkz. Settings.jsx'teki AYNI ilke).
const PCT_ONLY_STRATEGIES = ['rsi_rsima', 'rsima50'];

// Geçmiş sekmesi sayfalama — TradeHistory.jsx'teki AYNI PAGE_SIZES/pageNumbers deseni (paylaşılan
// bir utils dosyası yok, o dosyadaki yaklaşımla tutarlı kalınır).
const PAGE_SIZES = [5, 20, 50, 100, 500];
function pageNumbers(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = [];
  if (current <= 4) {
    for (let i = 1; i <= 5; i++) pages.push(i);
    pages.push('…');
    pages.push(total);
  } else if (current >= total - 3) {
    pages.push(1);
    pages.push('…');
    for (let i = total - 4; i <= total; i++) pages.push(i);
  } else {
    pages.push(1);
    pages.push('…');
    for (let i = current - 1; i <= current + 1; i++) pages.push(i);
    pages.push('…');
    pages.push(total);
  }
  return pages;
}

function fmtMoney(n) { return n == null ? '—' : `${n >= 0 ? '+' : ''}${n.toFixed(2)}`; }
function fmtPct(n)   { return n == null ? '—' : `${(n * 100).toFixed(1)}%`; }
function fmtTs(ms) {
  if (ms == null || isNaN(ms)) return '—';
  const d = new Date(ms);
  return d.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: '2-digit' })
       + ' ' + d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
}
// Tarih/saat alanları kullanıcının Ayarlar > Genel'den seçtiği UTC ofsetiyle çalışır (bkz. yukarıdaki
// toLocal ile AYNI ofset — grafikteki mum zamanları da bununla gösterilir). datetime-local input'u
// saniyesiz/dilim bilgisiz "YYYY-MM-DDTHH:mm" bekler; toDateInput bunu kullanıcının yerel saatine
// göre üretir, localToUtcIso bunu backend'e göndermeden önce GERÇEK UTC ISO string'ine çevirir.
function toDateInput(d, offsetMinutes) { return new Date(d.getTime() + offsetMinutes * 60000).toISOString().slice(0, 16); }
function localToUtcIso(str, offsetMinutes) {
  if (!str) return str;
  const asIfUtc = Date.parse(str.length === 16 ? `${str}:00Z` : `${str}Z`);
  return isNaN(asIfUtc) ? str : new Date(asIfUtc - offsetMinutes * 60000).toISOString();
}

// ── CSV dışa aktarma ────────────────────────────────────────────────
function csvEscape(v) { return `"${String(v ?? '').replace(/"/g, '""')}"`; }

function tradesToCsv(trades, initialCapital, t) {
  const hasCapital = initialCapital != null && !Number.isNaN(initialCapital);
  const header = [t('backtest.colEntry'), t('backtest.colSide'), t('backtest.colEntryPrice'), t('backtest.colExit'), t('backtest.colExitPrice'), 'P&L', ...(hasCapital ? [t('backtest.colBalance')] : []), t('backtest.colReason')];
  let running = initialCapital ?? 0;
  const rows = trades.map(trade => {
    if (hasCapital) running += trade.pnl;
    return [
      fmtTs(trade.entryTs), trade.side?.toUpperCase() || '', trade.entryPrice, fmtTs(trade.exitTs), trade.exitPrice, trade.pnl,
      ...(hasCapital ? [running] : []), trade.exitReason,
    ];
  });
  // Excel'in Türkçe karakterleri doğru okuması için BOM eklenir.
  return String.fromCharCode(0xFEFF) + [header, ...rows].map(r => r.map(csvEscape).join(',')).join('\r\n');
}

function downloadCsv(filename, content) {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function defaultBaseForm(offsetMinutes) {
  const end   = new Date();
  const start = new Date(end.getTime() - 90 * 24 * 60 * 60 * 1000);
  // strategy kasıtlı olarak boş — kullanıcı açıkça seçmeden backtest çalıştırılamaz (bkz. kullanıcı kararı).
  return { symbol: 'BTCUSDT', interval: '5m', strategy: '', startDate: toDateInput(start, offsetMinutes), endDate: toDateInput(end, offsetMinutes) };
}
function defaultCapitalForm() {
  return { initialCapital: '1000', margin: '100', leverage: '5', feeRate: '0.0005' };
}
function configFieldsFor(strategy, t) {
  // rsi_rsima/rsima50: ortak alanlardan sadece sl_pct alınır, atr_period/atr_sl_mult hariç tutulur
  // (bu 2 stratejide ATR yedek-sigorta fallback'ı yok — bkz. PCT_ONLY_STRATEGIES). Diğer stratejiler
  // için davranış AYNEN korunur.
  const common = PCT_ONLY_STRATEGIES.includes(strategy)
    ? getCommonConfigFields(t).filter(f => f.key === 'sl_pct')
    : getCommonConfigFields(t);
  return [...(getStrategyConfigFields(t)[strategy] || []), ...common];
}
function buildConfigPayload(config) {
  const out = {};
  for (const [k, v] of Object.entries(config)) {
    if (v === '' || v == null) continue;
    const n = Number(v);
    if (!isNaN(n)) out[k] = n;
  }
  return out;
}
function parseGrid(gridForm, fields) {
  const out = {};
  for (const f of fields) {
    const raw = gridForm[f.key];
    if (!raw) continue;
    const vals = String(raw).split(',').map(s => Number(s.trim())).filter(v => !isNaN(v));
    if (vals.length) out[f.key] = vals;
  }
  return out;
}
function authHeaders(token) {
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
}

// fetch cevabını güvenli şekilde JSON'a çevirir. Sunucu HTML döndürürse (504 gateway timeout,
// 404 "Cannot POST" vb.) ham "Unexpected token '<'" hatası yerine anlaşılır bir mesaj verir.
async function parseJsonResponse(res, t) {
  const text = await res.text();
  let data;
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    throw new Error(t('backtest.invalidServerResponse', { status: res.status }));
  }
  if (!res.ok) throw new Error(data.error || t('backtest.serverError', { status: res.status }));
  return data;
}

// ── Ortak form parçaları ──────────────────────────────────────────

function BaseFields({ form, onChange }) {
  const { t } = useTranslation();
  return (
    <>
      <div className="form-group">
        <label>{t('backtest.pair')}</label>
        <input
          className="input"
          placeholder={t('backtest.pairPlaceholder')}
          value={form.symbol}
          onChange={e => onChange({ symbol: e.target.value.toUpperCase() })}
        />
      </div>
      <div className="form-group">
        <label>{t('backtest.period')}</label>
        <div className="interval-btn-group">
          {getIntervals(t).map(iv => (
            <button
              key={iv.value} type="button"
              className={`interval-btn ${form.interval === iv.value ? 'active' : ''}`}
              onClick={() => onChange({ interval: iv.value })}
            >{iv.label}</button>
          ))}
        </div>
      </div>
      <div className="form-group">
        <label>{t('backtest.strategy')}</label>
        <div className="leverage-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
          {STRATEGIES.map(s => (
            <button
              key={s.value} type="button"
              className={`leverage-btn ${form.strategy === s.value ? 'active' : ''}`}
              onClick={() => onChange({ strategy: s.value })}
            >{s.label}</button>
          ))}
        </div>
      </div>
      <div className="form-group" style={{ display: 'flex', gap: 8 }}>
        <div style={{ flex: 1 }}>
          <label>{t('backtest.startDate')}</label>
          <input className="input" type="datetime-local" value={form.startDate} onChange={e => onChange({ startDate: e.target.value })} />
        </div>
        <div style={{ flex: 1 }}>
          <label>{t('backtest.endDate')}</label>
          <input className="input" type="datetime-local" value={form.endDate} onChange={e => onChange({ endDate: e.target.value })} />
        </div>
      </div>
    </>
  );
}

function CapitalFields({ form, onChange }) {
  const { t } = useTranslation();
  const set = (k) => (e) => onChange({ [k]: e.target.value });
  return (
    <div className="form-group config-grid">
      <div>
        <label>{t('backtest.initialCapital')}</label>
        <div className="input-suffix"><input className="input" type="number" value={form.initialCapital} onChange={set('initialCapital')} /><span className="suffix-label">USDT</span></div>
      </div>
      <div>
        <label>{t('backtest.marginPerTrade')}</label>
        <div className="input-suffix"><input className="input" type="number" value={form.margin} onChange={set('margin')} /><span className="suffix-label">USDT</span></div>
      </div>
      <div>
        <label>{t('backtest.leverage')}</label>
        <div className="input-suffix"><input className="input" type="number" value={form.leverage} onChange={set('leverage')} /><span className="suffix-label">×</span></div>
      </div>
      <div>
        <label>{t('backtest.feeRate')}</label>
        <div className="input-suffix"><input className="input" type="number" step="0.0001" value={form.feeRate} onChange={set('feeRate')} /><span className="suffix-label">{t('backtest.feeRateUnit')}</span></div>
      </div>
    </div>
  );
}

function ConfigFields({ strategy, config, onChange }) {
  const { t } = useTranslation();
  const fields = configFieldsFor(strategy, t);
  return (
    <div className="config-grid">
      {fields.map(f => (
        <div key={f.key} className="settings-field">
          <label className="settings-label">{f.label}</label>
          <input
            className="settings-input" type="number" step="any"
            value={config[f.key] ?? ''}
            placeholder={f.def}
            onChange={e => onChange({ ...config, [f.key]: e.target.value })}
          />
        </div>
      ))}
    </div>
  );
}

function ParamGridFields({ strategy, grid, onChange }) {
  const { t } = useTranslation();
  const fields = configFieldsFor(strategy, t);
  return (
    <div className="config-grid">
      {fields.map(f => (
        <div key={f.key} className="settings-field">
          <label className="settings-label">{f.label}</label>
          <input
            className="settings-input"
            value={grid[f.key] ?? ''}
            placeholder={t('backtest.gridPlaceholder', { ex: f.sweepEx || `${f.def},${f.def}` })}
            onChange={e => onChange({ ...grid, [f.key]: e.target.value })}
          />
        </div>
      ))}
      <span className="settings-hint" style={{ gridColumn: '1 / -1' }}>
        {t('backtest.gridHint')}
      </span>
    </div>
  );
}

// ── Equity curve mini grafik ──────────────────────────────────────

// lightweight-charts CSS değişkenlerini anlamaz — bkz. TradeChartModal.jsx/Chart.jsx'teki aynı desen.
function getEquityChartColors(theme) {
  const isDark = theme === 'dark';
  return {
    bg:     isDark ? '#1a2028' : '#ffffff',
    text:   isDark ? '#e2e8f0' : '#1e293b',
    grid:   isDark ? '#232a33' : '#f1f5f9',
    border: isDark ? '#313a46' : '#e2e8f0',
    line:   isDark ? '#2dd4bf' : '#0d9488',
  };
}

function EquityCurveChart({ points }) {
  const { theme } = useTheme();
  const { utcOffsetMinutes } = useUserSettings();
  const themeRef = useRef(theme);
  themeRef.current = theme;
  const containerRef = useRef(null);
  const chartRef      = useRef(null);

  useEffect(() => {
    if (!containerRef.current) return;
    const C = getEquityChartColors(themeRef.current);
    const chart = createChart(containerRef.current, {
      width: containerRef.current.clientWidth, height: 220,
      layout: { background: { color: C.bg }, textColor: C.text, fontSize: 10 },
      grid:   { vertLines: { color: C.grid }, horzLines: { color: C.grid } },
      rightPriceScale: { borderColor: C.border },
      timeScale:       { borderColor: C.border, timeVisible: true, secondsVisible: false },
    });
    const series = chart.addSeries(LineSeries, { color: C.line, lineWidth: 2, priceLineVisible: false });
    chartRef.current = { chart, series };

    const onResize = () => { if (containerRef.current) chart.applyOptions({ width: containerRef.current.clientWidth }); };
    window.addEventListener('resize', onResize);
    return () => { window.removeEventListener('resize', onResize); chart.remove(); chartRef.current = null; };
  }, []);

  // Tema değişince mevcut grafik yeniden oluşturulmaz — sadece renkler applyOptions ile güncellenir.
  useEffect(() => {
    if (!chartRef.current) return;
    const C = getEquityChartColors(theme);
    chartRef.current.chart.applyOptions({
      layout: { background: { color: C.bg }, textColor: C.text },
      grid:   { vertLines: { color: C.grid }, horzLines: { color: C.grid } },
      rightPriceScale: { borderColor: C.border },
      timeScale:       { borderColor: C.border },
    });
    chartRef.current.series.applyOptions({ color: C.line });
  }, [theme]);

  useEffect(() => {
    if (!chartRef.current || !points?.length) return;
    const data = [];
    let lastTime = null;
    for (const p of points) {
      const t = toLocal(p.ts, utcOffsetMinutes);
      if (lastTime != null && t <= lastTime) continue; // kesin artan zaman damgası gerekli
      data.push({ time: t, value: p.equity });
      lastTime = t;
    }
    chartRef.current.series.setData(data);
    chartRef.current.chart.timeScale().fitContent();
  }, [points, utcOffsetMinutes]);

  if (!points?.length) return null;
  return <div ref={containerRef} className="equity-chart" />;
}

// ── Metrik özet kartları ──────────────────────────────────────────

function MetricGrid({ metrics }) {
  const { t } = useTranslation();
  if (!metrics) return null;
  const items = [
    { label: t('backtest.metricTrades'),     value: metrics.totalTrades },
    { label: t('backtest.metricWinRate'),    value: fmtPct(metrics.winRate) },
    { label: t('backtest.metricTotalPnl'),       value: fmtMoney(metrics.totalPnl), cls: metrics.totalPnl >= 0 ? 'pnl-pos' : 'pnl-neg' },
    { label: t('backtest.metricFinalEquity'),     value: metrics.finalEquity != null ? fmtMoney(metrics.finalEquity) : '—', cls: metrics.totalPnl >= 0 ? 'pnl-pos' : 'pnl-neg' },
    { label: t('backtest.metricReturn'),           value: metrics.returnPct != null ? `${metrics.returnPct >= 0 ? '+' : ''}${metrics.returnPct.toFixed(2)}%` : '—' },
    { label: t('backtest.metricProfitFactor'),    value: metrics.profitFactor != null ? metrics.profitFactor.toFixed(2) : '∞' },
    { label: t('backtest.metricMaxDrawdown'),   value: metrics.maxDrawdownPct != null ? `${metrics.maxDrawdownPct.toFixed(2)}%` : '—' },
    { label: t('backtest.metricExpectancy'),   value: fmtMoney(metrics.expectancy) },
    { label: t('backtest.metricTotalFees'),  value: metrics.totalFees != null ? metrics.totalFees.toFixed(2) : '—' },
  ];
  return (
    <>
      {metrics.bankrupt && (
        <div className="msg msg-error">
          {t('backtest.bankruptWarning', { date: fmtTs(metrics.bankruptAt) })}
        </div>
      )}
      <div className="metric-grid">
        {items.map(it => (
          <div key={it.label} className="metric-card">
            <div className="metric-label">{it.label}</div>
            <div className={`metric-value ${it.cls || ''}`}>{it.value}</div>
          </div>
        ))}
      </div>
    </>
  );
}

// ── İşlem listesi (basit sayfalama) ────────────────────────────────

function TradeTable({ trades, symbol, interval, strategy, token, initialCapital, config }) {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [chartTrade, setChartTrade] = useState(null);
  const pageSize = 20;
  useEffect(() => { setPage(1); }, [trades]);

  if (!trades?.length) return <div className="empty-state">{t('backtest.emptyTrades')}</div>;

  const totalPages = Math.max(1, Math.ceil(trades.length / pageSize));
  const safePage    = Math.min(page, totalPages);
  const paged       = trades.slice((safePage - 1) * pageSize, safePage * pageSize);

  // Başlangıç sermayesi sabit varsayılarak her işlem sonrası kümülatif bakiye — TAM listeye göre
  // hesaplanır (sadece görünen sayfaya göre değil), aksi halde sayfa değişince bakiye sıfırlanmış gibi görünür.
  const hasCapital = initialCapital != null && !Number.isNaN(initialCapital);
  let running = initialCapital ?? 0;
  const balances = hasCapital ? trades.map(t => (running += t.pnl)) : null;
  const pageStart = (safePage - 1) * pageSize;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
        <button
          className="btn-refresh"
          onClick={() => downloadCsv(`backtest_islemler_${Date.now()}.csv`, tradesToCsv(trades, initialCapital, t))}
        >
          {t('backtest.csvDownload')}
        </button>
      </div>
      {chartTrade && (
        <TradeChartModal
          trade={{
            symbol, interval: interval || '5m', strategy, side: chartTrade.side,
            entryTs: chartTrade.entryTs, exitTs: chartTrade.exitTs ?? null,
            entryPrice: chartTrade.entryPrice, exitPrice: chartTrade.exitPrice ?? null,
            slPrice: chartTrade.slPrice ?? null, tpPrice: chartTrade.tpPrice ?? null, pnl: chartTrade.pnl,
            exitReason: chartTrade.exitReason ?? null, config: config || null,
          }}
          token={token}
          onClose={() => setChartTrade(null)}
        />
      )}
      <div className="table-scroll">
        <table className="trade-table">
          <thead>
            <tr>
              <th>{t('backtest.colEntry')}</th><th>{t('backtest.colSide')}</th><th>{t('backtest.colEntryPrice')}</th><th>{t('backtest.colExit')}</th><th>{t('backtest.colExitPrice')}</th><th>P&amp;L</th>
              {hasCapital && <th>{t('backtest.colBalance')}</th>}
              <th>{t('backtest.colReason')}</th><th></th>
            </tr>
          </thead>
          <tbody>
            {paged.map((tr, i) => (
              <tr key={i}>
                <td style={{ color: 'var(--muted)' }}>{fmtTs(tr.entryTs)}</td>
                <td>
                  <span className={`position-side side-${tr.side}`} style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, display: 'inline-block' }}>
                    {tr.side?.toUpperCase()}
                  </span>
                </td>
                <td>{tr.entryPrice?.toFixed(6)}</td>
                <td style={{ color: 'var(--muted)' }}>{fmtTs(tr.exitTs)}</td>
                <td>{tr.exitPrice?.toFixed(6)}</td>
                <td className={tr.pnl >= 0 ? 'pnl-pos' : 'pnl-neg'}>{fmtMoney(tr.pnl)}</td>
                {hasCapital && (
                  <td className={balances[pageStart + i] >= initialCapital ? 'pnl-pos' : 'pnl-neg'} style={{ fontWeight: 700 }}>
                    {fmtMoney(balances[pageStart + i])}
                  </td>
                )}
                <td style={{ color: 'var(--muted)', fontSize: 11 }}>{tr.exitReason}</td>
                <td>
                  {token && (
                    <button
                      className="btn-refresh"
                      title={t('backtest.visualizeTitle')}
                      onClick={() => setChartTrade(tr)}
                    >
                      📈
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className="pagination-bar">
          <span className="pagination-info">{(safePage - 1) * pageSize + 1}–{Math.min(safePage * pageSize, trades.length)} / {trades.length}</span>
          <div className="pagination-controls">
            <button className="pg-btn" disabled={safePage === 1} onClick={() => setPage(p => p - 1)}>‹</button>
            <span style={{ fontSize: 12, color: 'var(--muted)', padding: '0 8px' }}>{safePage}/{totalPages}</span>
            <button className="pg-btn" disabled={safePage === totalPages} onClick={() => setPage(p => p + 1)}>›</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Sekme: Tekli Test ───────────────────────────────────────────────

function RunTab({ token }) {
  const { t } = useTranslation();
  const { utcOffsetMinutes } = useUserSettings();
  const [form,    setForm]    = useState(() => defaultBaseForm(utcOffsetMinutes));
  const [config,  setConfig]  = useState({});
  const [capital, setCapital] = useState(defaultCapitalForm);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);
  const [result,  setResult]  = useState(null);

  const run = async () => {
    setLoading(true); setError(null);
    try {
      if (!form.strategy) throw new Error(t('backtest.errNoStrategy'));
      const body = {
        symbol: form.symbol, interval: form.interval, strategy: form.strategy,
        startDate: localToUtcIso(form.startDate, utcOffsetMinutes), endDate: localToUtcIso(form.endDate, utcOffsetMinutes),
        config: buildConfigPayload(config),
        initialCapital: Number(capital.initialCapital), margin: Number(capital.margin),
        leverage: Number(capital.leverage), feeRate: Number(capital.feeRate),
      };
      const res  = await fetch(`${API_URL}/backtest/run`, { method: 'POST', headers: authHeaders(token), body: JSON.stringify(body) });
      const data = await parseJsonResponse(res, t);
      setResult(data);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <BaseFields form={form} onChange={p => setForm(f => ({ ...f, ...p }))} />
      <CapitalFields form={capital} onChange={p => setCapital(f => ({ ...f, ...p }))} />
      <ConfigFields strategy={form.strategy} config={config} onChange={setConfig} />
      <button className="btn btn-start" disabled={loading || !form.strategy} onClick={run}>
        {loading ? t('backtest.running') : t('backtest.runButton')}
      </button>
      {error && <div className="msg msg-error">{error}</div>}
      {result && (
        <>
          <MetricGrid metrics={result.metrics} />
          <EquityCurveChart points={result.equityCurve} />
          <DataVerifyButton runId={result.id} token={token} />
          <TradeTable
            trades={result.trades} symbol={form.symbol} interval={form.interval} strategy={form.strategy} token={token}
            initialCapital={result.metrics ? result.metrics.finalEquity - result.metrics.totalPnl : null}
            config={buildConfigPayload(config)}
          />
        </>
      )}
    </div>
  );
}

// ── Sekme: Parametre Taraması ───────────────────────────────────────

function SweepTab({ token }) {
  const { t } = useTranslation();
  const { utcOffsetMinutes } = useUserSettings();
  const [form,    setForm]    = useState(() => defaultBaseForm(utcOffsetMinutes));
  const [grid,    setGrid]    = useState({});
  const [capital, setCapital] = useState(defaultCapitalForm);
  const [rankBy,  setRankBy]  = useState('totalPnl');
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);
  const [result,  setResult]  = useState(null);
  const [detail,  setDetail]  = useState(null);
  const [showHelp, setShowHelp] = useState(false);

  const run = async () => {
    setLoading(true); setError(null); setResult(null); setDetail(null);
    try {
      if (!form.strategy) throw new Error(t('backtest.errNoStrategy'));
      const paramGrid = parseGrid(grid, configFieldsFor(form.strategy, t));
      if (Object.keys(paramGrid).length === 0) throw new Error(t('backtest.errNoParam'));
      const body = {
        symbol: form.symbol, interval: form.interval, strategy: form.strategy,
        startDate: localToUtcIso(form.startDate, utcOffsetMinutes), endDate: localToUtcIso(form.endDate, utcOffsetMinutes), paramGrid, rankBy,
        initialCapital: Number(capital.initialCapital), margin: Number(capital.margin),
        leverage: Number(capital.leverage), feeRate: Number(capital.feeRate),
      };
      const res  = await fetch(`${API_URL}/backtest/sweep`, { method: 'POST', headers: authHeaders(token), body: JSON.stringify(body) });
      const data = await parseJsonResponse(res, t);
      setResult(data);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const openDetail = async (id) => {
    if (detail?.id === id) { setDetail(null); return; }
    setDetail({ id, loading: true, data: null });
    try {
      const res  = await fetch(`${API_URL}/backtest/runs/${id}`, { headers: authHeaders(token) });
      const data = await parseJsonResponse(res, t);
      setDetail({ id, loading: false, data: data.result });
    } catch (err) { setDetail({ id, loading: false, data: null, error: err.message }); }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {showHelp && <SweepHelp initialTab={form.strategy} onClose={() => setShowHelp(false)} />}
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button className="btn-refresh" onClick={() => setShowHelp(true)}>{t('backtest.help')}</button>
      </div>
      <BaseFields form={form} onChange={p => setForm(f => ({ ...f, ...p }))} />
      <CapitalFields form={capital} onChange={p => setCapital(f => ({ ...f, ...p }))} />
      <div className="settings-field">
        <label className="settings-label">{t('backtest.rankMetric')}</label>
        <select className="settings-input" value={rankBy} onChange={e => setRankBy(e.target.value)}>
          {getRankOptions(t).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>
      <ParamGridFields strategy={form.strategy} grid={grid} onChange={setGrid} />
      <button className="btn btn-start" disabled={loading || !form.strategy} onClick={run}>
        {loading ? t('backtest.sweeping') : t('backtest.sweepButton')}
      </button>
      {error && <div className="msg msg-error">{error}</div>}
      {result && (
        <div className="table-scroll">
          <table className="trade-table">
            <thead>
              <tr><th>#</th><th>{t('backtest.colConfig')}</th><th>{t('backtest.colTrades')}</th><th>{t('backtest.colWinRate')}</th><th>{t('backtest.metricTotalPnl')}</th><th>Profit Factor</th><th>{t('backtest.colMaxDD')}</th><th></th></tr>
            </thead>
            <tbody>
              {result.results.map((r, i) => (
                <React.Fragment key={r.id}>
                  <tr>
                    <td>{i + 1}</td>
                    <td style={{ fontSize: 11, fontFamily: 'var(--mono)' }}>
                      {Object.entries(r.config).map(([k, v]) => `${k}=${v}`).join(', ') || t('backtest.default')}
                    </td>
                    <td>{r.metrics.totalTrades}</td>
                    <td>{fmtPct(r.metrics.winRate)}</td>
                    <td className={r.metrics.totalPnl >= 0 ? 'pnl-pos' : 'pnl-neg'}>{fmtMoney(r.metrics.totalPnl)}</td>
                    <td>{r.metrics.profitFactor != null ? r.metrics.profitFactor.toFixed(2) : '∞'}</td>
                    <td>{r.metrics.maxDrawdownPct != null ? `${r.metrics.maxDrawdownPct.toFixed(2)}%` : '—'}</td>
                    <td><button className="btn-refresh" onClick={() => openDetail(r.id)}>{detail?.id === r.id ? t('backtest.colClose') : t('backtest.colDetail')}</button></td>
                  </tr>
                  {detail?.id === r.id && (
                    <tr><td colSpan={8}>
                      {detail.loading ? t('backtest.loading') : detail.error ? (
                        <div className="msg msg-error">{detail.error}</div>
                      ) : detail.data && (
                        <>
                          <EquityCurveChart points={detail.data.equityCurve} />
                          <DataVerifyButton runId={r.id} token={token} />
                          <TradeTable
                            trades={detail.data.trades} symbol={form.symbol} interval={form.interval} strategy={form.strategy} token={token}
                            initialCapital={detail.data.metrics ? detail.data.metrics.finalEquity - detail.data.metrics.totalPnl : null}
                            config={r.config}
                          />
                        </>
                      )}
                    </td></tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Sekme: Walk-Forward ─────────────────────────────────────────────

function WalkForwardTab({ token }) {
  const { t } = useTranslation();
  const { utcOffsetMinutes } = useUserSettings();
  const [form,      setForm]      = useState(() => defaultBaseForm(utcOffsetMinutes));
  const [grid,      setGrid]      = useState({});
  const [capital,   setCapital]   = useState(defaultCapitalForm);
  const [trainDays, setTrainDays] = useState('30');
  const [testDays,  setTestDays]  = useState('7');
  const [rankBy,    setRankBy]    = useState('totalPnl');
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState(null);
  const [result,    setResult]    = useState(null);

  const run = async () => {
    setLoading(true); setError(null); setResult(null);
    try {
      if (!form.strategy) throw new Error(t('backtest.errNoStrategy'));
      const paramGrid = parseGrid(grid, configFieldsFor(form.strategy, t));
      if (Object.keys(paramGrid).length === 0) throw new Error(t('backtest.errNoParam'));
      const body = {
        symbol: form.symbol, interval: form.interval, strategy: form.strategy,
        startDate: localToUtcIso(form.startDate, utcOffsetMinutes), endDate: localToUtcIso(form.endDate, utcOffsetMinutes), paramGrid, rankBy,
        trainDays: Number(trainDays), testDays: Number(testDays),
        initialCapital: Number(capital.initialCapital), margin: Number(capital.margin),
        leverage: Number(capital.leverage), feeRate: Number(capital.feeRate),
      };
      const res  = await fetch(`${API_URL}/backtest/walkforward`, { method: 'POST', headers: authHeaders(token), body: JSON.stringify(body) });
      const data = await parseJsonResponse(res, t);
      setResult(data);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <BaseFields form={form} onChange={p => setForm(f => ({ ...f, ...p }))} />
      <CapitalFields form={capital} onChange={p => setCapital(f => ({ ...f, ...p }))} />
      <div className="form-group" style={{ display: 'flex', gap: 8 }}>
        <div style={{ flex: 1 }}>
          <label>{t('backtest.trainWindow')}</label>
          <input className="input" type="number" value={trainDays} onChange={e => setTrainDays(e.target.value)} />
        </div>
        <div style={{ flex: 1 }}>
          <label>{t('backtest.testWindow')}</label>
          <input className="input" type="number" value={testDays} onChange={e => setTestDays(e.target.value)} />
        </div>
      </div>
      <div className="settings-field">
        <label className="settings-label">{t('backtest.rankMetricTrain')}</label>
        <select className="settings-input" value={rankBy} onChange={e => setRankBy(e.target.value)}>
          {getRankOptions(t).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>
      <ParamGridFields strategy={form.strategy} grid={grid} onChange={setGrid} />
      <button className="btn btn-start" disabled={loading || !form.strategy} onClick={run}>
        {loading ? t('backtest.running') : t('backtest.walkforwardButton')}
      </button>
      {error && <div className="msg msg-error">{error}</div>}
      {result && (
        <>
          <div className="metric-grid">
            <div className="metric-card"><div className="metric-label">{t('backtest.walkWindow')}</div><div className="metric-value">{result.summary.validWindows}/{result.summary.totalWindows}</div></div>
            <div className="metric-card"><div className="metric-label">{t('backtest.walkAvgInSample')}</div><div className="metric-value">{fmtMoney(result.summary.avgInSample)}</div></div>
            <div className="metric-card"><div className="metric-label">{t('backtest.walkAvgOutOfSample')}</div><div className={`metric-value ${result.summary.avgOutOfSample >= 0 ? 'pnl-pos' : 'pnl-neg'}`}>{fmtMoney(result.summary.avgOutOfSample)}</div></div>
            <div className="metric-card"><div className="metric-label">{t('backtest.walkOverfitRatio')}</div><div className="metric-value">{result.summary.overfitRatio != null ? result.summary.overfitRatio.toFixed(2) : '—'}</div></div>
            <div className="metric-card"><div className="metric-label">{t('backtest.walkOosPositive')}</div><div className="metric-value">{fmtPct(result.summary.outOfSamplePositiveWindowRatio)}</div></div>
          </div>
          <span className="settings-hint">
            {t('backtest.walkOverfitHint')}
          </span>
          <div className="table-scroll">
            <table className="trade-table">
              <thead>
                <tr><th>{t('backtest.colTrainRange')}</th><th>{t('backtest.colTestRange')}</th><th>{t('backtest.colSelectedConfig')}</th><th>{t('backtest.colInSamplePnl')}</th><th>{t('backtest.colOutOfSamplePnl')}</th><th>{t('backtest.colOosWinRate')}</th></tr>
              </thead>
              <tbody>
                {result.windows.map((w, i) => (
                  <tr key={i}>
                    <td style={{ fontSize: 11, color: 'var(--muted)' }}>{fmtTs(w.trainStart)} → {fmtTs(w.trainEnd)}</td>
                    <td style={{ fontSize: 11, color: 'var(--muted)' }}>{fmtTs(w.testStart)} → {fmtTs(w.testEnd)}</td>
                    <td style={{ fontSize: 11, fontFamily: 'var(--mono)' }}>
                      {w.config ? Object.entries(w.config).map(([k, v]) => `${k}=${v}`).join(', ') : '—'}
                    </td>
                    <td>{w.inSample ? fmtMoney(w.inSample.totalPnl) : '—'}</td>
                    <td className={w.outOfSample ? (w.outOfSample.totalPnl >= 0 ? 'pnl-pos' : 'pnl-neg') : ''}>
                      {w.outOfSample ? fmtMoney(w.outOfSample.totalPnl) : (w.skipped || '—')}
                    </td>
                    <td>{w.outOfSample ? fmtPct(w.outOfSample.winRate) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

// ── Doğrulama Logları (OKX veri-bazlı deterministik kontrol sonuçları) ────────────

function verifLogsToCsv(rows, t) {
  const header = [
    'Backtest ID', t('backtest.colSymbol'), t('backtest.period'), t('backtest.colStrategyShort'), `${t('backtest.colTrades')} #`, t('backtest.colSide'),
    t('backtest.colEntryTime'), t('backtest.colEntryPrice'), 'Low', 'High', t('backtest.colFits'),
    t('backtest.colExitTime'), t('backtest.colExitPrice'), 'Low', 'High', t('backtest.colFits'),
  ];
  const yes = t('common.yes').toUpperCase();
  const no  = t('common.no').toUpperCase();
  const body = rows.map(r => [
    r.backtest_run_id, r.symbol, r.interval, r.strategy, r.trade_index, r.side,
    fmtTs(parseInt(r.entry_ts, 10)), r.entry_price, r.entry_candle_low, r.entry_candle_high,
    r.entry_in_range == null ? '—' : (r.entry_in_range ? yes : no),
    r.exit_ts ? fmtTs(parseInt(r.exit_ts, 10)) : '—', r.exit_price ?? '—', r.exit_candle_low ?? '—', r.exit_candle_high ?? '—',
    r.exit_in_range == null ? '—' : (r.exit_in_range ? yes : no),
  ]);
  return String.fromCharCode(0xFEFF) + [header, ...body].map(r => r.map(csvEscape).join(',')).join('\r\n');
}

function VerificationLogsModal({ token, onClose }) {
  const { t } = useTranslation();
  const { offset: dragOffset, onMouseDown: handleHeaderMouseDown } = useDraggable('.settings-close, .btn-refresh');
  useTrackPageView('Backtest - Dogrulama Loglari');
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    fetch(`${API_URL}/backtest/verify-data-logs`, { headers: authHeaders(token) })
      .then(res => parseJsonResponse(res, t))
      .then(data => setRows(Array.isArray(data) ? data : []))
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [token]); // eslint-disable-line

  const failedCount = rows.filter(r => r.entry_in_range === false || r.exit_in_range === false).length;

  // Portal ile document.body'ye render edilir — bkz. TradeChartModal.jsx/SweepHelp.jsx'teki aynı yorum
  // (Backtest penceresinin transform'lu sürükleme stili, portal olmadan bu overlay'i sıkıştırırdı).
  return createPortal(
    <div className="log-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{
        background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12,
        transform: `translate(${dragOffset.x}px, ${dragOffset.y}px)`,
        width: 1100, maxWidth: '95vw', maxHeight: '85vh', overflowY: 'auto',
        boxShadow: '0 24px 60px rgba(0,0,0,.18)', padding: 20, boxSizing: 'border-box',
      }}>
        <div
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, cursor: 'move' }}
          onMouseDown={handleHeaderMouseDown}
        >
          <p className="card-title" style={{ marginBottom: 0 }}>{t('backtest.verifLogsTitle')}</p>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button
              className="btn-refresh" disabled={!rows.length}
              onClick={() => downloadCsv(`dogrulama_loglari_${Date.now()}.csv`, verifLogsToCsv(rows, t))}
            >
              {t('backtest.csvDownload')}
            </button>
            <button className="settings-close" onClick={onClose}>✕</button>
          </div>
        </div>

        {loading ? t('backtest.loading') : error ? (
          <div className="msg msg-error">{error}</div>
        ) : rows.length === 0 ? (
          <div className="empty-state">{t('backtest.verifLogsEmpty')}</div>
        ) : (
          <>
            <div className={`msg ${failedCount === 0 ? 'msg-success' : 'msg-error'}`} style={{ marginBottom: 10 }}>
              {t('backtest.verifLogsRecords', { n: rows.length })} · {failedCount === 0 ? t('backtest.verifLogsAllOk') : t('backtest.verifLogsFail', { n: failedCount })}
            </div>
            <div className="table-scroll">
              <table className="trade-table">
                <thead>
                  <tr>
                    <th>{t('backtest.colBacktest')}</th><th>#</th><th>{t('backtest.colSide')}</th>
                    <th>{t('backtest.colEntryTime')}</th><th>{t('backtest.colEntry')}</th><th>{t('backtest.colCandleRange')}</th><th>{t('backtest.colFits')}</th>
                    <th>{t('backtest.colExitTime')}</th><th>{t('backtest.colExit')}</th><th>{t('backtest.colCandleRange')}</th><th>{t('backtest.colFits')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(r => (
                    <tr key={r.id}>
                      <td style={{ fontSize: 11 }}>{r.symbol} · {r.interval} · {r.strategy} (#{r.backtest_run_id})</td>
                      <td>{r.trade_index}</td>
                      <td>
                        <span className={`position-side side-${r.side}`} style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, display: 'inline-block' }}>
                          {r.side?.toUpperCase()}
                        </span>
                      </td>
                      <td style={{ color: 'var(--muted)', fontSize: 11 }}>{fmtTs(parseInt(r.entry_ts, 10))}</td>
                      <td>{parseFloat(r.entry_price).toFixed(4)}</td>
                      <td style={{ fontSize: 11, color: 'var(--muted)' }}>{fmt2(r.entry_candle_low)} – {fmt2(r.entry_candle_high)}</td>
                      <td>{r.entry_in_range == null ? '—' : r.entry_in_range ? <span style={{ color: 'var(--accent)' }}>✓</span> : <span style={{ color: 'var(--red)' }}>✕</span>}</td>
                      <td style={{ color: 'var(--muted)', fontSize: 11 }}>{r.exit_ts ? fmtTs(parseInt(r.exit_ts, 10)) : '—'}</td>
                      <td>{r.exit_price != null ? parseFloat(r.exit_price).toFixed(4) : '—'}</td>
                      <td style={{ fontSize: 11, color: 'var(--muted)' }}>{r.exit_candle_low != null ? `${fmt2(r.exit_candle_low)} – ${fmt2(r.exit_candle_high)}` : '—'}</td>
                      <td>{r.exit_in_range == null ? '—' : r.exit_in_range ? <span style={{ color: 'var(--accent)' }}>✓</span> : <span style={{ color: 'var(--red)' }}>✕</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}

function fmt2(n) { return n == null ? '—' : parseFloat(n).toFixed(2); }

// Bir backtest_runs kaydını OKX'in gerçek mum verisiyle (deterministik, AI/OCR yok) doğrulayan
// buton + özet. runId hazır olur olmaz (Tekli Test'te "Çalıştır" sonrası, Geçmiş/Tarama'da her
// zaman) kullanılabilir — kendi state'ini yönetir, her yerde aynı davranışı garanti eder.
function DataVerifyButton({ runId, token }) {
  const { t } = useTranslation();
  const [state, setState] = useState(null); // { loading, summary, error }

  const runDataVerify = async () => {
    setState({ loading: true });
    try {
      const res  = await fetch(`${API_URL}/backtest/verify-data`, {
        method: 'POST', headers: authHeaders(token), body: JSON.stringify({ runId }),
      });
      const data = await parseJsonResponse(res, t);
      setState({ loading: false, summary: data });
    } catch (err) {
      setState({ loading: false, error: err.message });
    }
  };

  if (!runId) return null;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '10px 0', flexWrap: 'wrap' }}>
      <button className="btn-refresh" disabled={state?.loading} onClick={runDataVerify}>
        {state?.loading ? t('backtest.verifying') : t('backtest.verifyDataButton')}
      </button>
      {state?.error && <span style={{ color: 'var(--red)', fontSize: 12 }}>{state.error}</span>}
      {state?.summary && (
        <span style={{ fontSize: 12, fontWeight: 700, color: state.summary.allPassed ? 'var(--accent)' : 'var(--red)' }}>
          {state.summary.allPassed ? '✓' : '⚠'}{' '}
          {t('backtest.verifySummary', { ok: state.summary.comparableCount - state.summary.failedCount, total: state.summary.comparableCount })}
          {state.summary.failedCount > 0 && t('backtest.verifyMismatch', { n: state.summary.failedCount })}
        </span>
      )}
    </div>
  );
}

// ── Sekme: Geçmiş ────────────────────────────────────────────────────

function HistoryTab({ token }) {
  const { t } = useTranslation();
  const { utcOffsetMinutes } = useUserSettings();
  const [runs,           setRuns]           = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [detail,         setDetail]         = useState(null);
  const [selected,       setSelected]       = useState(new Set());
  const [showLogs,       setShowLogs]       = useState(false);
  const [symbolFilter,   setSymbolFilter]   = useState('all');
  const [strategyFilter, setStrategyFilter] = useState('all');
  const [dateFrom,       setDateFrom]       = useState(''); // 'YYYY-MM-DD', saatsiz
  const [dateTo,         setDateTo]         = useState('');
  const [pnlOp,          setPnlOp]          = useState('gt'); // 'gt' | 'lt'
  const [pnlValue,       setPnlValue]       = useState('');
  const [page,           setPage]           = useState(1);
  const [pageSize,       setPageSize]       = useState(5);

  const fetchRuns = () => {
    setLoading(true);
    fetch(`${API_URL}/backtest/runs`, { headers: authHeaders(token) })
      .then(r => r.json())
      .then(data => { setRuns(Array.isArray(data) ? data : []); setSelected(new Set()); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(fetchRuns, [token]); // eslint-disable-line

  // Kayıtlar veya filtreler değişince 1. sayfaya dön (TradeHistory.jsx'teki AYNI davranış).
  useEffect(() => { setPage(1); }, [runs, symbolFilter, strategyFilter, dateFrom, dateTo, pnlOp, pnlValue]);

  const openDetail = async (id) => {
    if (detail?.id === id) { setDetail(null); return; }
    setDetail({ id, loading: true, data: null });
    try {
      const res  = await fetch(`${API_URL}/backtest/runs/${id}`, { headers: authHeaders(token) });
      const data = await parseJsonResponse(res, t);
      setDetail({ id, loading: false, data });
    } catch (err) { setDetail({ id, loading: false, data: null, error: err.message }); }
  };

  // Sembol/Strateji filtreleri — seçenekler mevcut kayıtlardan türetilir (alfabetik), böylece
  // artık kullanılmayan bir strateji (ör. kaldırılan rsi50) geçmişte varsa filtre listesinde de
  // görünür. 'all' = filtre yok.
  const symbolOptions   = [...new Set(runs.map(r => r.symbol))].sort();
  const strategyOptions = [...new Set(runs.map(r => r.strategy))].sort();
  // Tarih filtresi saat/dakika içermez, sadece gün seçilir; kullanıcının Ayarlar > Genel'deki
  // UTC ofsetine göre günün başı/sonu hesaplanır (bkz. dosya başındaki toDateInput/localToUtcIso
  // ile AYNI ofset yaklaşımı). Kayıt, ARALIK'ı (start_ts→end_ts) seçilen [dateFrom, dateTo]
  // aralığıyla KESİŞİYORSA gösterilir — böylece seçilen aralığı "kapsayan" tüm kayıtlar dahil olur.
  const dateFromMs = dateFrom ? Date.parse(`${dateFrom}T00:00:00Z`) - utcOffsetMinutes * 60000 : null;
  const dateToMs   = dateTo   ? Date.parse(`${dateTo}T23:59:59.999Z`) - utcOffsetMinutes * 60000 : null;
  const pnlValueNum = pnlValue === '' ? null : Number(pnlValue);
  const filtersActive = symbolFilter !== 'all' || strategyFilter !== 'all'
    || !!dateFrom || !!dateTo || pnlValueNum != null;
  const filteredRuns = runs.filter(r => {
    if (symbolFilter !== 'all' && r.symbol !== symbolFilter) return false;
    if (strategyFilter !== 'all' && r.strategy !== strategyFilter) return false;
    if (dateFromMs != null && parseInt(r.end_ts, 10) < dateFromMs) return false;
    if (dateToMs != null && parseInt(r.start_ts, 10) > dateToMs) return false;
    if (pnlValueNum != null) {
      const pnl = r.metrics?.totalPnl;
      if (pnl == null) return false;
      if (pnlOp === 'gt' ? !(pnl > pnlValueNum) : !(pnl < pnlValueNum)) return false;
    }
    return true;
  });

  const totalPages = Math.max(1, Math.ceil(filteredRuns.length / pageSize));
  const safePage   = Math.min(page, totalPages);
  const pageStart  = (safePage - 1) * pageSize;
  const pagedRuns  = filteredRuns.slice(pageStart, pageStart + pageSize);
  const goTo = (p) => setPage(Math.max(1, Math.min(p, totalPages)));

  const toggleRow = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  // "Tümünü seç" TradeHistory.jsx'teki gibi sadece GÖRÜNEN sayfayı kapsar — diğer sayfalardaki
  // seçim korunur, bu yüzden ekleme/çıkarma mevcut seçime göre yapılır (üzerine yazmaz).
  const pageIds     = pagedRuns.map(r => r.id);
  const allChecked  = pageIds.length > 0 && pageIds.every(id => selected.has(id));
  const toggleAll = () => {
    setSelected(prev => {
      const next = new Set(prev);
      if (allChecked) pageIds.forEach(id => next.delete(id));
      else            pageIds.forEach(id => next.add(id));
      return next;
    });
  };

  const handleDelete = async () => {
    // Seçim varsa sadece seçilenler silinir. Seçim yokken ("Tümünü Sil"): bir filtre aktifse
    // kullanıcının GÖRDÜĞÜ (filtrelenmiş) kayıtlarla sınırlı silinir — filtre aktifken sessizce
    // görünmeyen kayıtları da silmek şaşırtıcı/riskli olurdu. Filtre yoksa gerçekten hepsi silinir.
    const ids   = selected.size > 0 ? [...selected] : (filtersActive ? filteredRuns.map(r => r.id) : null);
    const label = ids ? t('backtest.deleteConfirmSelected', { n: ids.length }) : t('backtest.deleteConfirmAll');
    if (!window.confirm(t('backtest.deleteConfirm', { label }))) return;
    await fetch(`${API_URL}/backtest/runs`, {
      method: 'DELETE', headers: authHeaders(token), body: JSON.stringify(ids ? { ids } : {}),
    });
    fetchRuns();
  };

  if (loading) return <div className="empty-state">{t('backtest.loading')}</div>;

  return (
    <div>
      {showLogs && <VerificationLogsModal token={token} onClose={() => setShowLogs(false)} />}
      <div className="history-header">
        <p className="card-title" style={{ marginBottom: 0 }}>{t('backtest.historyTitle')}</p>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button className="btn-refresh" onClick={() => setShowLogs(true)}>{t('backtest.verifyLogs')}</button>
          {selected.size > 0 && (
            <button className="btn-refresh" style={{ color: 'var(--danger)' }} onClick={handleDelete}>
              {t('backtest.deleteSelected', { n: selected.size })}
            </button>
          )}
          {selected.size === 0 && filteredRuns.length > 0 && (
            <button className="btn-refresh" style={{ color: 'var(--danger)' }} onClick={handleDelete}>
              {filtersActive ? t('backtest.deleteFiltered', { n: filteredRuns.length }) : t('backtest.deleteAll')}
            </button>
          )}
          <button className="btn-refresh" onClick={fetchRuns}>{t('backtest.refresh')}</button>
        </div>
      </div>

      {runs.length > 0 && (
        <div className="backtest-filter-row" style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
          <select className="settings-input" style={{ maxWidth: 130 }} value={symbolFilter} onChange={e => setSymbolFilter(e.target.value)}>
            <option value="all">{t('backtest.filterAllSymbols')}</option>
            {symbolOptions.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select className="settings-input" style={{ maxWidth: 160 }} value={strategyFilter} onChange={e => setStrategyFilter(e.target.value)}>
            <option value="all">{t('backtest.filterAllStrategies')}</option>
            {strategyOptions.map(s => <option key={s} value={s}>{STRATEGY_LABEL_MAP[s] || s}</option>)}
          </select>
          <span className="settings-unit">{t('backtest.filterDateFrom')}</span>
          <input
            className="input" type="date" style={{ maxWidth: 120 }}
            value={dateFrom} onChange={e => setDateFrom(e.target.value)}
          />
          <span className="settings-unit">{t('backtest.filterDateTo')}</span>
          <input
            className="input" type="date" style={{ maxWidth: 120 }}
            value={dateTo} onChange={e => setDateTo(e.target.value)}
          />
          <select className="settings-input" style={{ maxWidth: 62 }} value={pnlOp} onChange={e => setPnlOp(e.target.value)}>
            <option value="gt">PNL &gt;</option>
            <option value="lt">PNL &lt;</option>
          </select>
          <input
            className="settings-input" type="number" step="any" style={{ maxWidth: 80 }}
            placeholder={t('backtest.filterPnlPlaceholder')}
            value={pnlValue} onChange={e => setPnlValue(e.target.value)}
          />
          {filtersActive && (
            <button className="btn-refresh" onClick={() => {
              setSymbolFilter('all'); setStrategyFilter('all');
              setDateFrom(''); setDateTo(''); setPnlValue('');
            }}>
              {t('backtest.filterClear')}
            </button>
          )}
        </div>
      )}

      {runs.length === 0 ? (
        <div className="empty-state">{t('backtest.emptyHistory')}</div>
      ) : filteredRuns.length === 0 ? (
        <div className="empty-state">{t('backtest.emptyHistoryFiltered')}</div>
      ) : (
      <>
      <div className="table-scroll">
      {/* table-layout:fixed KASITLI — Detay satırındaki colSpan hücresi iç içe geniş bir tablo
          (TradeTable) barındırıyor; auto layout'ta bu, dış hücreyi (ve tüm modalı) içeriğe göre
          büyütüp taşırır. Fixed layout dış tabloyu modal genişliğine sabitler, iç tablo kendi
          .table-scroll'uyla (TradeTable içinde) ayrıca kayar. */}
      <table className="trade-table" style={{ tableLayout: 'fixed' }}>
        <thead>
          <tr>
            <th style={{ width: 32, textAlign: 'center' }}>
              <input type="checkbox" checked={allChecked} onChange={toggleAll} style={{ cursor: 'pointer', accentColor: 'var(--accent)' }} />
            </th>
            <th style={{ width: '13%' }}>{t('backtest.colDate')}</th>
            <th style={{ width: '11%' }}>{t('backtest.colSymbol')}</th>
            <th style={{ width: '13%' }}>{t('backtest.colStrategyShort')}</th>
            <th style={{ width: '28%' }}>{t('backtest.colRange')}</th>
            <th style={{ width: '9%' }}>{t('backtest.colTrades')}</th>
            <th style={{ width: '12%' }}>PnL</th>
            <th style={{ width: '10%' }}></th>
          </tr>
        </thead>
        <tbody>
          {pagedRuns.map(r => {
            const m = r.metrics;
            return (
              <React.Fragment key={r.id}>
                <tr>
                  <td style={{ textAlign: 'center' }}>
                    <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggleRow(r.id)} style={{ cursor: 'pointer', accentColor: 'var(--accent)' }} />
                  </td>
                  <td style={{ fontSize: 11, color: 'var(--muted)' }}>{fmtTs(new Date(r.created_at).getTime())}</td>
                  <td style={{ fontWeight: 700 }}>{r.symbol}</td>
                  <td>{STRATEGY_LABEL_MAP[r.strategy] || r.strategy}</td>
                  <td style={{ fontSize: 11, color: 'var(--muted)' }}>{fmtTs(parseInt(r.start_ts, 10))} → {fmtTs(parseInt(r.end_ts, 10))}</td>
                  <td>{m?.totalTrades ?? '—'}</td>
                  <td className={m?.totalPnl >= 0 ? 'pnl-pos' : 'pnl-neg'}>{m ? fmtMoney(m.totalPnl) : '—'}</td>
                  <td><button className="btn-refresh" onClick={() => openDetail(r.id)}>{detail?.id === r.id ? t('backtest.colClose') : t('backtest.colDetail')}</button></td>
                </tr>
                {detail?.id === r.id && (
                  <tr><td colSpan={8}>
                    {detail.loading ? t('backtest.loading') : detail.error ? (
                      <div className="msg msg-error">{detail.error}</div>
                    ) : detail.data && (
                      detail.data.result.metrics ? (
                        <>
                          <MetricGrid metrics={detail.data.result.metrics} />
                          <EquityCurveChart points={detail.data.result.equityCurve} />
                          <DataVerifyButton runId={r.id} token={token} />
                          <TradeTable
                            trades={detail.data.result.trades} symbol={r.symbol} interval={r.interval} strategy={r.strategy} token={token}
                            initialCapital={detail.data.result.metrics ? detail.data.result.metrics.finalEquity - detail.data.result.metrics.totalPnl : null}
                            config={detail.data.config}
                          />
                        </>
                      ) : (
                        <div style={{ fontSize: 12, color: 'var(--muted)', padding: '8px 0' }}>
                          {t('backtest.notSingleTestNote')}
                        </div>
                      )
                    )}
                  </td></tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
      </div>

      <div className="pagination-bar">
        <div className="pagination-left">
          <span className="pagination-info">
            {pageStart + 1}–{Math.min(pageStart + pageSize, filteredRuns.length)} / {filteredRuns.length}
            {filteredRuns.length !== runs.length && ` ${t('tradeHistory.total', { n: runs.length })}`}
          </span>
          <select
            className="pagination-size"
            value={pageSize}
            onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
          >
            {PAGE_SIZES.map(s => (
              <option key={s} value={s}>{t('tradeHistory.rowsPerPage', { n: s })}</option>
            ))}
          </select>
        </div>

        <div className="pagination-controls">
          <button className="pg-btn" onClick={() => goTo(1)}            disabled={safePage === 1}>«</button>
          <button className="pg-btn" onClick={() => goTo(safePage - 1)} disabled={safePage === 1}>‹</button>

          {pageNumbers(safePage, totalPages).map((p, i) =>
            p === '…'
              ? <span key={`e${i}`} className="pg-ellipsis">…</span>
              : <button
                  key={p}
                  className={`pg-btn${p === safePage ? ' pg-btn-active' : ''}`}
                  onClick={() => goTo(p)}
                >{p}</button>
          )}

          <button className="pg-btn" onClick={() => goTo(safePage + 1)} disabled={safePage === totalPages}>›</button>
          <button className="pg-btn" onClick={() => goTo(totalPages)}   disabled={safePage === totalPages}>»</button>
        </div>
      </div>
      </>
      )}
    </div>
  );
}

// ── Ana Panel ─────────────────────────────────────────────────────

// Sekme etiketleri `t()`'a bağlı olduğu için bileşen içinde inşa edilir (bkz. getTabs).
const getTabs = (t) => [
  { id: 'run',         label: t('backtest.tabRun') },
  { id: 'sweep',        label: t('backtest.tabSweep') },
  // Şimdilik sekme çubuğundan gizlendi (kullanıcı isteği) — WalkForwardTab bileşeni, backend
  // /backtest/walkforward route'u ve runWalkForward tamamen yerinde duruyor, SİLİNMEDİ. İleride
  // tekrar geliştirmeye devam edilmek istenirse hidden:true kaldırılıp sekme geri açılabilir.
  { id: 'walkforward', label: t('backtest.tabWalkforward'), hidden: true },
  { id: 'history',      label: t('backtest.tabHistory') },
];

export default function BacktestPanel({ token, onClose }) {
  const { t } = useTranslation();
  const TABS = getTabs(t);
  const [tab, setTab] = useState('run');
  const { offset: dragOffset, onMouseDown: handleHeaderMouseDown } = useDraggable();
  useTrackPageView(`Backtest - ${TABS.find(tb => tb.id === tab)?.label || tab}`);

  return (
    <div className="backtest-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="backtest-modal" style={{ transform: `translate(${dragOffset.x}px, ${dragOffset.y}px)` }}>
        <div className="settings-header backtest-drag-handle" onMouseDown={handleHeaderMouseDown}>
          <span className="settings-title">{t('backtest.title')}</span>
          <button className="settings-close" onClick={onClose}>✕</button>
        </div>
        <div className="settings-tabs">
          {TABS.filter(tb => !tb.hidden).map(tb => (
            <button key={tb.id} className={`settings-tab ${tab === tb.id ? 'active' : ''}`} onClick={() => setTab(tb.id)}>
              {tb.label}
            </button>
          ))}
        </div>
        <div className="settings-body">
          {tab === 'run'         && <RunTab token={token} />}
          {tab === 'sweep'        && <SweepTab token={token} />}
          {tab === 'walkforward' && <WalkForwardTab token={token} />}
          {tab === 'history'      && <HistoryTab token={token} />}
        </div>
      </div>
    </div>
  );
}
